import {
  CustomMicrofrontendConfiguration,
  CustomMicrofrontendConfigurationZod,
  CustomMicrofrontendOptions,
} from './CustomMicrofrontendConfiguration';
import { CustomAlertApi } from '../../apis';
import { get, set } from 'lodash';
import { CustomError } from '@agilelab/plugin-wb-platform-common';
import { z, ZodError, ZodTypeAny } from 'zod';
import { createApiRef } from '@mainstage/core-plugin-api';

export type CustomFrontendApi = {
  /**
   * Generate the custom microfrontend options needed for a specific page.
   * This method should be used by passing first the target configurations, then the Zod
   * schema describing the shape of the input data accepted by that page.
   * The returned function only accepts data matching that schema, so passing the wrong
   * shape (or forgetting to pass it) is caught at compile time.
   *
   * An example could be:
   * ```typescript
   * const useCustomFrontend = customFrontendApi.makeCustomMicrofrontends(
   *   configurations,
   *   BuilderSystemPageInputSchema,
   * );
   * const customMicrofrontendConfigurations = useCustomFrontend({ entity });
   * ```
   */
  makeCustomMicrofrontends<Schema extends ZodTypeAny>(
    newConfiguration: CustomMicrofrontendConfiguration[],
    inputSchema: Schema,
  ): (data: z.infer<Schema>) => CustomMicrofrontendOptions[];
};

/**
 * The API reference for the CustomFrontendApi.
 * @public
 */
export const customFrontendApiRef = createApiRef<CustomFrontendApi>({
  id: 'plugin.platform.custom-frontend',
});

export class CustomFrontendClient implements CustomFrontendApi {
  constructor(private readonly alertApi: CustomAlertApi) {}

  extractDataFromInput(
    input: unknown,
    config: CustomMicrofrontendConfiguration,
  ): Record<string, unknown> {
    const data: Record<string, unknown> = {};
    config.parameters.forEach(parameter => {
      set(data, parameter.name, get(input, parameter.valuePath));
    });
    return data;
  }

  generateErrorMessage(error: ZodError): string {
    const fieldErrors = error.flatten().fieldErrors;
    return [
      'Please fix the following configuration errors for the microfrontend definition:',
      ...Object.keys(fieldErrors).map(key => {
        return `- ${key}: ${fieldErrors[key]}`;
      }),
    ].join('\n');
  }

  makeCustomMicrofrontends<Schema extends ZodTypeAny>(
    newConfiguration: CustomMicrofrontendConfiguration[],
    inputSchema: Schema,
  ): (data: z.infer<Schema>) => CustomMicrofrontendOptions[] {
    return data => {
      const parsedInput = inputSchema.safeParse(data);
      return (
        newConfiguration?.flatMap(configuration => {
          const parsed =
            CustomMicrofrontendConfigurationZod.safeParse(configuration);
          if (!parsed.success) {
            this.alertApi.post({
              error: new CustomError(
                'Failed to load configuration for microfrontend',
                this.generateErrorMessage(parsed.error),
              ),
              severity: 'error',
            });
            return [];
          }
          const config = parsed.data;
          set(
            config,
            'data',
            parsedInput.data
              ? this.extractDataFromInput(parsedInput.data, config)
              : undefined,
          );
          return [config as CustomMicrofrontendOptions];
        }) ?? []
      );
    };
  }
}
